import Papa from "papaparse";

export interface ParsedData {
  rows: Record<string, unknown>[];
  columns: string[];
  headerRowIndex: number;
}

export const parseCSV = (
  file: File,
  headerRowIndex?: number,
  columnCount?: number,
): Promise<ParsedData> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(
            new Error(`CSV parsing errors: ${results.errors.map((e) => e.message).join(", ")}`),
          );
          return;
        }

        const rows = results.data as string[][];

        // Use provided header row index or auto-detect
        const actualHeaderRowIndex =
          headerRowIndex !== undefined ? headerRowIndex : detectHeaderRow(rows).headerRowIndex;
        const actualColumnCount = columnCount || Math.max(...rows.map((row) => row.length));

        // Get headers from the specified row
        const headerRow = rows[actualHeaderRowIndex] || [];
        const columns = headerRow
          .slice(0, actualColumnCount)
          .map((col, index) => (col ? String(col).trim() : `Column ${index + 1}`));

        // Convert to objects using the headers
        const dataRows = rows.slice(actualHeaderRowIndex + 1);
        const objectRows = dataRows.map((row) => {
          const obj: Record<string, unknown> = {};
          columns.forEach((col, index) => {
            obj[col] = row[index] || "";
          });
          return obj;
        });

        resolve({
          rows: objectRows,
          columns,
          headerRowIndex: actualHeaderRowIndex,
        });
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      },
    });
  });
};

export const parseXLSX = (
  file: File,
  sheetName?: string,
  headerRowIndex?: number,
  columnCount?: number,
): Promise<ParsedData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const XLSX = await import("xlsx");
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetNames = workbook.SheetNames;
        const selectedSheet = sheetName || sheetNames[0];
        const worksheet = workbook.Sheets[selectedSheet];

        if (!worksheet) {
          reject(new Error(`Sheet '${selectedSheet}' not found`));
          return;
        }

        // Convert to array of arrays
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        }) as string[][];
        const rows = jsonData.filter((row) =>
          row.some((cell) => cell !== undefined && cell !== ""),
        );

        // Use provided header row index or auto-detect
        const actualHeaderRowIndex =
          headerRowIndex !== undefined ? headerRowIndex : detectHeaderRow(rows).headerRowIndex;
        const actualColumnCount = columnCount || Math.max(...rows.map((row) => row.length));

        // Get headers from the specified row
        const headerRow = rows[actualHeaderRowIndex] || [];
        const columns = headerRow
          .slice(0, actualColumnCount)
          .map((col, index) => (col ? String(col).trim() : `Column ${index + 1}`));

        // Convert to objects using the headers
        const dataRows = rows.slice(actualHeaderRowIndex + 1);
        const objectRows = dataRows.map((row) => {
          const obj: Record<string, unknown> = {};
          columns.forEach((col, index) => {
            obj[col] = row[index] || "";
          });
          return obj;
        });

        resolve({
          rows: objectRows,
          columns,
          headerRowIndex: actualHeaderRowIndex,
        });
      } catch (error) {
        reject(
          new Error(
            `XLSX parsing failed: ${error instanceof Error ? error.message : "Unknown error"}`,
          ),
        );
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsArrayBuffer(file);
  });
};

export const getXLSXSheets = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const XLSX = await import("xlsx");
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        resolve(workbook.SheetNames);
      } catch (error) {
        reject(
          new Error(
            `Failed to read XLSX sheets: ${error instanceof Error ? error.message : "Unknown error"}`,
          ),
        );
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsArrayBuffer(file);
  });
};

const detectHeaderRow = (rows: string[][]): { headerRowIndex: number; columns: string[] } => {
  // Look for header row (usually row 0-3)
  for (let i = 0; i < Math.min(4, rows.length); i++) {
    const row = rows[i];
    if (row && row.length > 0) {
      // Check if this row looks like a header (has meaningful column names)
      const potentialHeaders = row.map((cell) => String(cell || "").trim());
      const hasValidHeaders = potentialHeaders.some(
        (header) => header.length > 0 && header.length < 50 && /^[A-Za-z\s\-_]+$/.test(header), // Basic validation for header-like text
      );

      if (hasValidHeaders) {
        return {
          headerRowIndex: i,
          columns: potentialHeaders,
        };
      }
    }
  }

  // Fallback: use first row or generate column names
  const firstRow = rows[0] || [];
  const columns =
    firstRow.length > 0 ? firstRow.map((_, index) => `Column ${index + 1}`) : ["Column 1"];

  return {
    headerRowIndex: 0,
    columns,
  };
};
